import { useEffect, useState } from "react";
import { Box, Collapsible, Text } from "@chakra-ui/react";
import { HiChevronDown } from "react-icons/hi2";

export const SectionCard = ({ children, ...props }) => (
    <Box bg="bg.card" borderRadius={12} p={5} border="1px solid" borderColor="border.primary" {...props}>
        {children}
    </Box>
);

export const ItemCard = ({ children, ...props }) => (
    <Box
        bg="bg.secondary"
        borderRadius={8}
        p={4}
        border="1px solid"
        borderColor="border.primary"
        borderLeftWidth="3px"
        borderLeftColor="purple.800"
        transition="border-color 0.2s"
        _hover={{ borderLeftColor: "purple.500" }}
        {...props}
    >
        {children}
    </Box>
);

/**
 * A collapsible form section. Replaces the old SectionCard + SectionHeader pair so long
 * steps read as a scannable outline instead of one continuous wall of fields. No number
 * badge here on purpose -- the left-hand step navigation already numbers things, and a
 * second, differently-scoped numbering inside each section read as confusing/incorrect.
 * `forceOpen` re-expands the section (without permanently locking it) whenever it starts
 * carrying a validation error, so a collapsed section can never hide a problem from the user.
 */
export const CollapsibleSection = ({ title, subtitle, icon: Icon, defaultOpen = false, forceOpen = false, action, children }) => {
    const [open, setOpen] = useState(defaultOpen);

    useEffect(() => {
        if (forceOpen) setOpen(true);
    }, [forceOpen]);

    return (
        <Box
            bg="bg.card"
            borderRadius={12}
            border="1px solid"
            borderColor={open ? "border.secondary" : "border.primary"}
            overflow="hidden"
            transition="border-color 0.2s"
        >
            <Collapsible.Root open={open} onOpenChange={(e) => setOpen(e.open)}>
                <Collapsible.Trigger asChild>
                    <Box
                        as="button"
                        type="button"
                        aria-expanded={open}
                        w="full"
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        gap={3}
                        px={5}
                        py={4}
                        textAlign="left"
                        _hover={{ bg: "bg.highlight" }}
                        _active={{ bg: "bg.highlight", transform: "scale(0.997)" }}
                        transition="background 0.15s, transform 0.1s"
                    >
                        <Box display="flex" alignItems="center" gap={3} minW={0}>
                            {Icon && (
                                <Box
                                    flexShrink={0}
                                    color={open ? "purple.300" : "purple.400"}
                                    display="flex"
                                    aria-hidden="true"
                                    transition="color 0.2s"
                                >
                                    <Icon size={18} />
                                </Box>
                            )}
                            <Box minW={0}>
                                <Text color="white" fontWeight={700} fontSize="sm">{title}</Text>
                                {subtitle && <Text fontSize="xs" color="text.grey" mt={0.5}>{subtitle}</Text>}
                            </Box>
                        </Box>
                        <Box display="flex" alignItems="center" gap={2} flexShrink={0}>
                            {action}
                            <Box
                                aria-hidden="true"
                                color="text.grey"
                                display="flex"
                                transform={open ? "rotate(180deg)" : "none"}
                                transition="transform 0.25s ease"
                            >
                                <HiChevronDown size={18} />
                            </Box>
                        </Box>
                    </Box>
                </Collapsible.Trigger>
                <Collapsible.Content>
                    <Box px={5} pb={6} pt={4} borderTop="1px solid" borderColor="border.primary">
                        {children}
                    </Box>
                </Collapsible.Content>
            </Collapsible.Root>
        </Box>
    );
};

export const SectionHeader = ({ number, title, subtitle, action }) => (
    <Box display="flex" alignItems="center" justifyContent="space-between" mb={5}>
        <Box display="flex" alignItems="center" gap={3}>
            {number !== undefined && (
                <Box
                    flexShrink={0}
                    w={6}
                    h={6}
                    borderRadius="full"
                    bg="purple.900"
                    color="purple.400"
                    fontSize="xs"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    fontWeight={600}
                >
                    {number}
                </Box>
            )}
            <Box>
                <Text color="white" fontWeight={700}>{title}</Text>
                {subtitle && <Text fontSize="xs" color="text.grey" mt={0.5}>{subtitle}</Text>}
            </Box>
        </Box>
        {action}
    </Box>
);

export const Divider = (props) => <Box as="hr" borderColor="border.primary" my={5} {...props} />;

/**
 * Standardised container for a cluster of related on/off switches. Every step used to lay
 * these out with a one-off gridTemplateColumns per section (2, 3 or 4 columns, different
 * breakpoints each time), which is exactly what made toggles feel scattered across the form.
 * A single auto-fit grid + a visible boundary makes every switch cluster look and behave
 * the same everywhere, and reads as one decision panel instead of loose floating controls.
 */
export const SwitchGroup = ({ children, ...props }) => (
    <Box
        display="grid"
        gridTemplateColumns="repeat(auto-fit, minmax(200px, 1fr))"
        gap={3}
        p={3}
        border="1px solid"
        borderColor="border.primary"
        borderRadius={10}
        transition="border-color 0.2s"
        _hover={{ borderColor: "border.secondary" }}
        {...props}
    >
        {children}
    </Box>
);

export const SubsectionTitle = ({ children }) => (
    <Box display="flex" alignItems="center" gap={2} mb={3}>
        <Box w={1} h={4} bg="purple.500" borderRadius="full" />
        <Text fontSize="xs" color="text.grey" textTransform="uppercase" letterSpacing="wider" fontWeight={500}>
            {children}
        </Text>
    </Box>
);

export const Grid2 = ({ children, ...props }) => (
    <Box display="grid" gridTemplateColumns={{ base: "1fr", sm: "1fr 1fr" }} gap={4} {...props}>
        {children}
    </Box>
);

export const Grid3 = ({ children, ...props }) => (
    <Box display="grid" gridTemplateColumns={{ base: "1fr", sm: "1fr 1fr 1fr" }} gap={4} {...props}>
        {children}
    </Box>
);
